import { Navigate, NavLink, Outlet, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/features/auth/useAuth';
import { ChallengesPage } from './ChallengesPage';
import { PrizeVaultPage } from './PrizeVaultPage';
import { RedemptionsPage } from './RedemptionsPage';

/**
 * Portal Content (SPEC §9) — what customers see in the portal's Arcade and
 * Prize Vault, managed by staff: Challenges, the Prize Vault catalogue, and
 * the redemption queue.
 *
 * The whole area sits behind `portal.manage`. The sidebar already hides the
 * tab without it; this stops the screens being reachable by URL. The API
 * refuses the same key, which is the gate that actually matters.
 */
export const PORTAL_CONTENT_TABS = ['challenges', 'prizes', 'redemptions'] as const;

/**
 * Tabs and redirects address their screens ABSOLUTELY, and must keep doing so.
 *
 * App.tsx mounts this area as `<Route path="/portal-content/*">`. A relative
 * `to` inside a splat route resolves against the whole matched pathname — splat
 * included — so `to="prizes"` from `/portal-content/challenges` aimed at
 * `/portal-content/challenges/prizes`. No route matches that, so the `*`
 * fallback fired, and because its `to` was relative too it appended another
 * segment, matched `*` again, and never stopped: the URL grew without bound.
 * Absolute paths cannot express that bug.
 */
const AREA = '/portal-content';
const LANDING = `${AREA}/challenges`;

function PortalContentLayout() {
  const { t } = useTranslation();
  const { can } = useAuth();

  if (!can('portal.manage')) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('portalContent.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('portalContent.noPermission')}</p>
      </div>
    );
  }

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
      isActive
        ? 'border-primary font-medium text-content'
        : 'border-transparent text-content-secondary hover:text-content'
    }`;

  return (
    <div className="flex h-full flex-col">
      <header className="px-4 pt-4">
        <h1 className="text-xl font-semibold text-content">{t('portalContent.title')}</h1>

        <nav
          className="mt-3 flex gap-1 border-b border-subtle"
          aria-label={t('portalContent.title')}
        >
          {PORTAL_CONTENT_TABS.map((tab) => (
            <NavLink key={tab} to={`${AREA}/${tab}`} className={tabClass}>
              {t(`portalContent.tabs.${tab}`)}
            </NavLink>
          ))}
        </nav>
      </header>

      <div className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}

export function PortalContentRoutes() {
  return (
    <Routes>
      <Route element={<PortalContentLayout />}>
        <Route index element={<Navigate to={LANDING} replace />} />
        <Route path="challenges" element={<ChallengesPage />} />
        <Route path="prizes" element={<PrizeVaultPage />} />
        <Route path="redemptions" element={<RedemptionsPage />} />
        <Route path="*" element={<Navigate to={LANDING} replace />} />
      </Route>
    </Routes>
  );
}
